import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { aiRequestsApi, type AiRequest } from '@/api/aiRequests';
import { aiWalletApi } from '@/api/aiWallet';
import { automationApi } from '@/api/automation';
import { payeesApi, type Payee } from '@/api/payees';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { MoneyText } from '@/components/MoneyText';
import { ErrorState } from '@/components/ErrorState';
import { StatusChip } from '@/components/StatusChip';
import { civilToday } from '@/features/audit/dates';
import { addCivilDays } from '@/shared/servedWindow';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { TableCard } from '@/components/TableCard';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import {
  activeCount,
  civilDateParam,
  defaultsOf,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { AiRequestDetail } from './AiRequestDetail';
import { featureLabel } from './aiFeatureLabel';
import { aiSessionLabel } from './aiRequestSession';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; items: AiRequest[]; nextCursor: string | null; knownFeatures: string[] }
  | { status: 'error'; error: unknown };

const FILTERS = ['from', 'to', 'feature'] as const;

/**
 * AI activity `/me/ai-requests` — disclosure of ids/counts that went into each request
 * (TMMAPP-0030) and, where stored, the exact text that was sent (TMMAPP-0044). Feature filter is
 * built from values present in the response (never a hardcoded list).
 *
 * An assistant charge says who asked and where (TMMAPP-0148 U1 item 9, `aiSessionLabel`): the owner
 * of a virtual account pays for its assistant sessions, members' calls included.
 */
export function AiHistoryPage() {
  const { t } = useTranslation();
  const me = useMe();
  const title = usePageTitle({ title: t('aiHistory.title') });
  return (
    <>
      <PageHeader title={title} subtitle={t('aiHistory.intro')} />
      {me.tier === 'GUEST' ? (
        <Typography variant="body1">{t('aiHistory.empty')}</Typography>
      ) : (
        <OwnedEnvelopesProvider>
          <AiHistoryPageInner />
        </OwnedEnvelopesProvider>
      )}
    </>
  );
}

function AiHistoryPageInner() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  // `chargedMinor` is in `AiRequestView.currency` when served (TMMAPI-0162 U6), else the meter's.
  // The meter is read only for a charged row with no currency. Unread or failed, the amount is a
  // plain number (MoneyText with no currency), never a guessed one.
  const [walletCurrency, setWalletCurrency] = useState<string | null | undefined>(undefined);
  // The filters are in the URL; the date range is omitted while it is the last 90 days.
  const schema = useMemo(() => {
    const today = civilToday(zone);
    const options = { allowEmpty: true };
    return {
      from: civilDateParam(addCivilDays(today, -90), options),
      to: civilDateParam(today, options),
      feature: stringParam(),
    };
  }, [zone]);
  const [filters, setUrl] = useUrlState(schema);
  const { from, to, feature } = filters;
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [expanded, setExpanded] = useState<number | null>(null);
  const [featureDescriptions, setFeatureDescriptions] = useState<Map<string, string>>(new Map());
  const [payees, setPayees] = useState<Payee[] | null>(null);
  const payeesLoading = useRef(false);

  const load = useCallback(
    async (cursor?: string) => {
      if (cursor === undefined) setState({ status: 'loading' });
      try {
        const page = await aiRequestsApi.list({
          from,
          to,
          timeZone: zone,
          ...(feature.length > 0 ? { feature } : {}),
          ...(cursor !== undefined ? { cursor } : {}),
          limit: 50,
        });
        setState((prev) => {
          const prior = cursor !== undefined && prev.status === 'ready' ? prev.items : [];
          const items = [...prior, ...page.items];
          const known = new Set<string>(
            cursor !== undefined && prev.status === 'ready' ? prev.knownFeatures : [],
          );
          for (const row of page.items) {
            if (row.feature.length > 0) known.add(row.feature);
          }
          return {
            status: 'ready',
            items,
            nextCursor: page.nextCursor,
            knownFeatures: [...known].sort(),
          };
        });
      } catch (error) {
        setState({ status: 'error', error });
      }
    },
    [feature, from, to, zone],
  );

  useEffect(() => {
    queueMicrotask(() => {
      void load();
    });
  }, [load]);

  const needsWalletCurrency =
    state.status === 'ready' &&
    state.items.some((row) => row.chargedMinor !== null && row.currency === null);
  useEffect(() => {
    if (!needsWalletCurrency || walletCurrency !== undefined) return;
    let cancelled = false;
    void aiWalletApi
      .get()
      .then((wallet) => {
        if (!cancelled) setWalletCurrency(wallet?.currency ?? null);
      })
      .catch(() => {
        if (!cancelled) setWalletCurrency(null);
      });
    return () => {
      cancelled = true;
    };
  }, [needsWalletCurrency, walletCurrency]);

  useEffect(() => {
    let cancelled = false;
    void automationApi
      .get()
      .then((catalog) => {
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const group of catalog.groups) {
          for (const item of group.items) {
            if (!item.key.startsWith('AI_')) continue;
            const featureKey = item.key.slice('AI_'.length);
            if (featureKey.length > 0 && item.description.length > 0) {
              next.set(featureKey, item.description);
            }
          }
        }
        setFeatureDescriptions(next);
      })
      .catch(() => {
        /* catalogue is best-effort — no description when absent */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ensurePayees = useCallback(() => {
    if (payees !== null || payeesLoading.current) return;
    payeesLoading.current = true;
    void payeesApi
      .list()
      .then((rows) => setPayees(rows))
      .catch(() => setPayees([]))
      .finally(() => {
        payeesLoading.current = false;
      });
  }, [payees]);

  const featureOptions = useMemo(() => {
    const known = state.status === 'ready' ? state.knownFeatures : [];
    // A linked feature is always an option, so the select never holds a value it does not list.
    return feature.length > 0 && !known.includes(feature) ? [...known, feature].sort() : known;
  }, [state, feature]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
        <TextField
          type="date"
          label={t('aiHistory.from')}
          value={from}
          onChange={(event) => setUrl({ from: event.target.value })}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label={t('aiHistory.to')}
          value={to}
          onChange={(event) => setUrl({ to: event.target.value })}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="ai-feature">{t('aiHistory.feature')}</InputLabel>
          <Select
            labelId="ai-feature"
            label={t('aiHistory.feature')}
            value={feature}
            onChange={(event) => setUrl({ feature: event.target.value })}
          >
            <MenuItem value="">{t('aiHistory.anyFeature')}</MenuItem>
            {featureOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {featureLabel(t, value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ClearFiltersButton
          active={activeCount(schema, filters, FILTERS)}
          onClear={() => setUrl(defaultsOf(schema))}
        />
      </Stack>

      {state.status === 'loading' && <Skeleton variant="rounded" height={160} />}
      {state.status === 'error' && (
        <ErrorState
          variant="section"
          error={state.error}
          what={t('errors.what.aiHistory')}
          onRetry={() => void load()}
        />
      )}
      {state.status === 'ready' && state.items.length === 0 && (
        <Typography>{t('aiHistory.empty')}</Typography>
      )}
      {state.status === 'ready' && state.items.length > 0 && (
        <TableCard title={t('aiHistory.listTitle')} name={t('aiHistory.title')}>
          <Table size="small" aria-label={t('aiHistory.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('aiHistory.colWhen')}</TableCell>
                <TableCell>{t('aiHistory.colFeature')}</TableCell>
                <TableCell>{t('aiHistory.colModel')}</TableCell>
                <TableCell>{t('aiHistory.colOutcome')}</TableCell>
                <TableCell align="right">{t('aiHistory.colCharged')}</TableCell>
                <ActionsHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.items.map((row) => {
                const open = expanded === row.id;
                const description = featureDescriptions.get(row.feature) ?? null;
                const where = aiSessionLabel(t, row);
                return (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell>{formatInstant(row.createdAt, locale, zone)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <span>{featureLabel(t, row.feature)}</span>
                          {where !== null && (
                            <Typography variant="body2" data-ai-session="">
                              {where}
                            </Typography>
                          )}
                          {description !== null && (
                            <Typography variant="body2" color="text.secondary">
                              {description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell>
                        <StatusChip domain="aiRequest" status={row.outcome} />
                      </TableCell>
                      {/* What the person paid (MM-D-140): never a provider cost or a token count. */}
                      <TableCell align="right" data-money="">
                        <MoneyText
                          minor={row.chargedMinor}
                          currency={row.currency ?? walletCurrency ?? null}
                          variant="body2"
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => setExpanded(open ? null : row.id)}>
                          {t('aiHistory.viewSent')}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open} unmountOnExit>
                          <AiRequestDetail
                            request={row}
                            open={open}
                            payees={payees}
                            onNeedPayees={ensurePayees}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableCard>
      )}
      {state.status === 'ready' && state.nextCursor !== null && (
        <Button onClick={() => void load(state.nextCursor!)}>{t('aiHistory.loadMore')}</Button>
      )}
    </Stack>
  );
}
